/** Application shells: one identity, a product label per surface, attention before navigation. */
import { useEffect, useState, type ReactNode } from 'react';
import { Link, NavLink, useLocation } from 'react-router';

export interface NavItem {
  label: string;
  /** Route inside this surface. */
  to?: string;
  /** Another P1Plug surface or an external page. */
  href?: string;
  /** Match the route exactly (for the index route). */
  end?: boolean;
  /** Optional count shown after the label, e.g. open attention cases. */
  count?: number;
}

export interface NavGroup {
  title?: string;
  items: NavItem[];
}

/** The wordmark is plain type until a logo file exists (design system: iconography and imagery). */
export function Brand({ product, to = '/' }: { product: string; to?: string }) {
  return (
    <Link className="p1x-brand" to={to} aria-label={`P1Plug ${product}, home`}>
      <span className="p1x-brand-name">P1Plug</span>
      <span className="p1x-brand-product">{product}</span>
    </Link>
  );
}

/** Marks every screen of a demo build. Demo data is never presented as real. */
export function DemoBanner({
  text = 'Demo data. Nothing on this page is a real device, customer or measurement.',
}: {
  text?: string;
}) {
  return (
    <p className="p1x-demo" role="note">
      <span className="p1x-demo-tag">Demo</span>
      <span>{text}</span>
    </p>
  );
}

function NavList({ groups, onNavigate }: { groups: NavGroup[]; onNavigate?: () => void }) {
  return (
    <>
      {groups.map((group, i) => (
        <div className="p1x-nav-group" key={group.title ?? i}>
          {group.title ? <p className="p1x-nav-title">{group.title}</p> : null}
          <ul role="list">
            {group.items.map((item) => (
              <li key={item.label}>
                {item.to !== undefined ? (
                  <NavLink className="p1x-nav-link" to={item.to} end={item.end} onClick={onNavigate}>
                    <span>{item.label}</span>
                    {item.count ? (
                      <span className="p1x-nav-count p1-num" aria-label={`${item.count} open`}>
                        {item.count}
                      </span>
                    ) : null}
                  </NavLink>
                ) : (
                  <a className="p1x-nav-link" href={item.href}>
                    <span>{item.label}</span>
                    <span className="p1x-nav-out" aria-label="opens another P1Plug application">
                      ↗
                    </span>
                  </a>
                )}
              </li>
            ))}
          </ul>
        </div>
      ))}
    </>
  );
}

export interface ShellProps {
  product: string;
  nav: NavGroup[];
  demo?: boolean;
  demoText?: string;
  /** Sidebar footer or top-bar end: environment, organisation, language. */
  meta?: ReactNode;
  children: ReactNode;
}

/** Professional shell: persistent left sidebar on wide screens, a menu on narrow ones. */
export function SidebarShell({ product, nav, demo, demoText, meta, children }: ShellProps) {
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();
  useEffect(() => setOpen(false), [pathname]);
  return (
    <div className="p1x-shell p1x-shell--sidebar">
      <a className="p1x-skip" href="#main">
        Skip to content
      </a>
      <header className="p1x-sidebar">
        <div className="p1x-sidebar-head">
          <Brand product={product} />
          <button
            className="p1x-menu-toggle"
            type="button"
            aria-expanded={open}
            aria-controls="p1x-nav"
            onClick={() => setOpen(!open)}
          >
            {open ? 'Close' : 'Menu'}
          </button>
        </div>
        <nav id="p1x-nav" className="p1x-nav" data-open={open} aria-label={`${product} navigation`}>
          <NavList groups={nav} onNavigate={() => setOpen(false)} />
          {meta ? <div className="p1x-sidebar-meta">{meta}</div> : null}
        </nav>
      </header>
      <div className="p1x-main-wrap">
        {demo ? <DemoBanner text={demoText} /> : null}
        <main id="main" className="p1x-main">
          {children}
        </main>
      </div>
    </div>
  );
}

/** Task shell: a slim top bar. Consumer and workstation surfaces have three or four destinations at most. */
export function TopShell({
  product,
  nav,
  demo,
  demoText,
  meta,
  children,
  width = 'regular',
  tabBar = false,
}: ShellProps & { width?: 'narrow' | 'regular' | 'wide'; tabBar?: boolean }) {
  const items = nav.flatMap((g) => g.items);
  const links = (className: string) =>
    items.map((item) =>
      item.to !== undefined ? (
        <NavLink key={item.label} className={className} to={item.to} end={item.end}>
          {item.label}
        </NavLink>
      ) : (
        <a key={item.label} className={className} href={item.href}>
          {item.label}
        </a>
      ),
    );
  return (
    <div className={`p1x-shell p1x-shell--top p1x-width-${width}${tabBar ? ' has-tabbar' : ''}`}>
      <a className="p1x-skip" href="#main">
        Skip to content
      </a>
      <header className="p1x-topbar">
        <div className="p1x-topbar-inner">
          <Brand product={product} />
          {items.length > 0 ? (
            <nav className="p1x-topnav" aria-label={`${product} navigation`}>
              {links('p1x-topnav-link')}
            </nav>
          ) : null}
          {meta ? <div className="p1x-topbar-meta">{meta}</div> : null}
        </div>
      </header>
      {demo ? <DemoBanner text={demoText} /> : null}
      <main id="main" className="p1x-main">
        {children}
      </main>
      {tabBar ? (
        <nav className="p1x-tabbar" aria-label={`${product} navigation`}>
          {links('p1x-tabbar-link')}
        </nav>
      ) : null}
    </div>
  );
}

export interface Crumb {
  label: string;
  to?: string;
}

/** Hierarchy, not history. */
export function Breadcrumbs({ items }: { items: Crumb[] }) {
  return (
    <nav className="p1x-crumbs" aria-label="Breadcrumb">
      <ol role="list">
        {items.map((c, i) => (
          <li key={c.label} aria-current={i === items.length - 1 ? 'page' : undefined}>
            {c.to ? <Link to={c.to}>{c.label}</Link> : <span>{c.label}</span>}
          </li>
        ))}
      </ol>
    </nav>
  );
}

/** One page title per screen; it names the primary object. */
export function PageHeader({
  title,
  lead,
  crumbs,
  actions,
  status,
}: {
  title: ReactNode;
  lead?: ReactNode;
  crumbs?: Crumb[];
  actions?: ReactNode;
  status?: ReactNode;
}) {
  return (
    <div className="p1x-page-header">
      {crumbs ? <Breadcrumbs items={crumbs} /> : null}
      <div className="p1x-page-header-row">
        <div className="p1x-page-header-text">
          <h1 className="p1x-page-title">{title}</h1>
          {lead ? <p className="p1x-lead">{lead}</p> : null}
        </div>
        {actions ? <div className="p1x-page-actions">{actions}</div> : null}
      </div>
      {status ? <div className="p1x-page-status">{status}</div> : null}
    </div>
  );
}
